import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js"
import { loadStripe, Stripe, StripeCardElementChangeEvent, StripeError } from "@stripe/stripe-js"
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react"
import api from "../../../api2"
import Button from "../../ui/Button"
import Spinner from "../../ui/Spinner"
import { AdminContext } from "~/contexts/AdminContext"
import Alert from "../../ui/Alert"
import ILineItem from "../../../interfaces/ILineItem"
import IPaymentMethods from "../../../interfaces/IPaymentMethods"
import { mapLineItemToParams } from "~/api2/orders"
import CheckboxWithLabelForForm from "../../ui/CheckboxWithLabelForForm"
import { RadioGroup } from "@headlessui/react"
import { PlusIcon } from "@heroicons/react/20/solid"
import { WifiIcon, CreditCardIcon } from "@heroicons/react/24/outline"
import ePaymentType from "../../../enums/ePaymentType"
import IOrder from "../../../interfaces/IOrder"
import PaymentMethodsList from "./CreditCardForm/PaymentMethodsList"
import { StripeCardElement } from "@stripe/stripe-js"
import { PaymentIntentResult } from "@stripe/stripe-js"
import { Combobox } from "@headlessui/react"
import classNames from "classnames"
import useWebsocket, { LiveWebAppNotification } from "~/hooks/useWebsocket"
import createSubscriptionLiveWebAppNotificationsChannel from "../../../channels/live_web_app_notifications_channel"
import PortalHoverTip from "~/components/ui/PortalHoverTip"
import { ExclamationCircleIcon } from "@heroicons/react/24/solid"
import { STATUSES } from "~/constants"

// TODO: this publick key will always be in the FE code, but it'd be nice to add
// it as an ENV to get it out of the codebase directly.
const stripePublicKey = import.meta.env.VITE_STRIPE_PUBKEY

type TPaymentMethodType = "link" | "reader" | "new" | "card-input" | "cash"

interface ComponentProps {
  ctaText: string
  paymentIntentId: string
  setPaymentIntentId: (intentId: string) => void
  onSuccess: () => void
  order: IOrder
  camperId: string
  isCamperPresent: boolean
  campingStyleId: string
  isTaxFeeEnabled: boolean
  isCreditCardFeeEnabled: boolean
  discountSlug: string
  lineItems: Array<Partial<ILineItem>>
  depositLineItems: Array<Partial<ILineItem>>
  paymentMethod: ePaymentType
  setPaymentMethod: (method: ePaymentType) => void
  paymentMethods: Array<IPaymentMethods>
  isNewReservation?: boolean
  errorMessage: string
  setErrorMessage: (message: string) => void
  setPaymentStatus: (status: string | null) => void
  setIntervalId: (id: any) => void
  setShowCloseButton: (show: boolean) => void
  width?: string
  isStore?: boolean
  isDisabledButton?: boolean
  minTotalInCents?: number
  isOrderRecurring?: boolean
  isEnabledAutoCharging?: boolean
  isCardInputValid?: boolean
  setIsCardInputValid?: React.Dispatch<React.SetStateAction<boolean>>
  hideCta?: boolean
  isCreateV2?: boolean
}

const StripeCardForm = forwardRef(
  (
    {
      ctaText,
      setPaymentIntentId,
      onSuccess,
      order,
      camperId,
      isCamperPresent,
      campingStyleId,
      isTaxFeeEnabled,
      isCreditCardFeeEnabled,
      discountSlug,
      lineItems,
      depositLineItems,
      paymentMethod,
      isNewReservation,
      setPaymentMethod,
      errorMessage,
      setErrorMessage,
      setPaymentStatus,
      setIntervalId,
      setShowCloseButton,
      width,
      isStore,
      minTotalInCents,
      isDisabledButton,
      isOrderRecurring,
      isEnabledAutoCharging,
      isCardInputValid,
      setIsCardInputValid,
      hideCta,
      isCreateV2,
    }: ComponentProps,
    ref,
  ) => {
    const { camp } = useContext(AdminContext)
    const { stripe_account_id } = camp

    const [stripePromise, setStripePromise] = useState<Promise<Stripe>>()

    useEffect(() => {
      setStripePromise(loadStripe(stripePublicKey, { stripeAccount: stripe_account_id }) as Promise<Stripe>)
    }, [])

    if (!stripePromise) {
      return (
        <div className="flex flex-col justify-center items-center">
          <Spinner size={5} />
          <div className="mt-1 text-gray-400 text-sm">Loading...</div>
        </div>
      )
    }

    return (
      <Elements stripe={stripePromise}>
        {order && <Form
          ref={ref}
          ctaText={ctaText}
          setPaymentIntentId={setPaymentIntentId}
          onSuccess={onSuccess}
          order={order}
          camperId={camperId}
          isCamperPresent={isCamperPresent}
          campingStyleId={campingStyleId}
          isTaxFeeEnabled={isTaxFeeEnabled}
          isCreditCardFeeEnabled={isCreditCardFeeEnabled}
          discountSlug={discountSlug}
          lineItems={lineItems}
          depositLineItems={depositLineItems}
          paymentMethod={paymentMethod}
          isNewReservation={isNewReservation}
          setPaymentMethod={setPaymentMethod}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          setPaymentStatus={setPaymentStatus}
          setIntervalId={setIntervalId}
          setShowCloseButton={setShowCloseButton}
          width={width}
          isStore={!!isStore}
          minTotalInCents={minTotalInCents}
          isDisabledButton={isDisabledButton}
          isOrderRecurring={!!isOrderRecurring}
          isEnabledAutoCharging={!!isEnabledAutoCharging}
          isCardInputValid={isCardInputValid}
          setIsCardInputValid={setIsCardInputValid}
          hideCta={hideCta}
          isCreateV2={isCreateV2}
        />}
      </Elements>
    )
  },
)

const Form = React.forwardRef(
  (
    {
      ctaText,
      setPaymentIntentId,
      onSuccess,
      order,
      camperId,
      isCamperPresent,
      campingStyleId,
      isTaxFeeEnabled,
      isCreditCardFeeEnabled,
      discountSlug,
      lineItems,
      depositLineItems,
      paymentMethod,
      isNewReservation,
      setPaymentMethod,
      errorMessage,
      setErrorMessage,
      setPaymentStatus,
      setIntervalId,
      setShowCloseButton,
      width,
      isStore,
      minTotalInCents,
      isDisabledButton,
      isOrderRecurring,
      isEnabledAutoCharging,
      isCardInputValid,
      setIsCardInputValid,
      hideCta,
      isCreateV2,
      reservationId
    }: {
      isDisabledButton?: boolean
      ctaText: string
      setPaymentIntentId: (intentId: string) => void
      onSuccess: () => void
      order: IOrder
      camperId: string
      isCamperPresent: boolean
      campingStyleId: string
      isTaxFeeEnabled: boolean
      isCreditCardFeeEnabled: boolean
      discountSlug: string
      lineItems: Array<Partial<ILineItem>>
      depositLineItems: Array<Partial<ILineItem>>
      isNewReservation?: boolean
      paymentMethod: ePaymentType
      setPaymentMethod: (method: ePaymentType) => void
      errorMessage: string
      setErrorMessage: (message: string) => void
      setPaymentStatus: (status: string) => void
      setIntervalId: (id: any) => void
      setShowCloseButton: (show: boolean) => void
      width?: string
      isStore: boolean
      minTotalInCents?: number
      isOrderRecurring: boolean
      isEnabledAutoCharging: boolean
      isCardInputValid?: boolean
      setIsCardInputValid?: React.Dispatch<React.SetStateAction<boolean>>
      hideCta?: boolean
      isCreateV2?: boolean
      reservationId?: string
    },
    ref,
  ) => {
    const { camp, reservationStates, } = useContext(AdminContext)
    const isCardReader = !!camp?.stripe_reader_id

    const [readers, setReaders] = useState([])
    const [selectedReaderId, setSelectedReaderId] = useState("")
    
    const saveReaderToLocalStorage = (readerId, campId) => {
      if (readerId && campId) {
        localStorage.setItem(`park_terminal_reader_${campId}`, readerId)
      }
    }
    
    const getSavedReaderFromLocalStorage = (campId) => {
      if (campId) {
        return localStorage.getItem(`park_terminal_reader_${campId}`)
      }
      return null
    }

    const [isDisabled, setIsDisabled] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const [isSavingCc, setIsSavingCc] = useState(false)
    const [isAutoCharging, setIsAutoCharging] = useState(isEnabledAutoCharging)
    const [localPaymentMethods, setLocalPaymentMethods] = useState<IPaymentMethods[]>([])
    const [hasPendingPayments, setHasPendingPayments] = useState<boolean>(false)
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<TPaymentMethodType>(
      (localPaymentMethods?.length > 0
        ? localPaymentMethods[0].id
        : isCardReader
          ? "reader"
          : "new") as TPaymentMethodType,
    )
    const isAutoChargingDisabled =
      (selectedPaymentMethodId == "new" || selectedPaymentMethodId == "reader") && !isSavingCc
    const stripe = useStripe()
    const elements = useElements()

    useEffect(() => {
      if (!localPaymentMethods?.length || !order?.id) {
        setSelectedPaymentMethodId(isCardReader ? "reader" : "new")
      } else {
        setSelectedPaymentMethodId(localPaymentMethods[0].id as TPaymentMethodType)
      }
    }, [localPaymentMethods])

    useEffect(() => {
      if (selectedPaymentMethodId === "reader") {
        setPaymentMethod(ePaymentType.CARD_READER)
      } else if ([ePaymentType.CARD_INPUT, ePaymentType.CARD_READER].includes(paymentMethod)) {
        setPaymentMethod(ePaymentType.CARD_INPUT)
      }
    }, [selectedPaymentMethodId])

    useEffect(() => {
      setIsDisabled(paymentMethod == "card-input" && selectedPaymentMethodId == "new")
      if (selectedPaymentMethodId == "new" && setIsCardInputValid) setIsCardInputValid(false)
    }, [paymentMethod, selectedPaymentMethodId])

    useEffect(() => {
      if (isCardReader) {
        fetchTerminalReaders()
      }
    }, [isCardReader, camp?.id])

    const fetchTerminalReaders = async () => {
      try {
        const response = await api.Terminal.fetchTerminalReaders(camp.id)
        setReaders(response.terminal_readers || [])
        
        if (response.terminal_readers && response.terminal_readers.length > 0) {
          const savedReaderId = getSavedReaderFromLocalStorage(camp.id)
          
          if (response.terminal_readers.length > 1 && savedReaderId && 
              response.terminal_readers.some(reader => reader.id === savedReaderId)) {
            setSelectedReaderId(savedReaderId)
          }
          else if (response.terminal_readers.length === 1) {
            setSelectedReaderId(response.terminal_readers[0].id)
            saveReaderToLocalStorage(response.terminal_readers[0].id, camp.id)
          }
          else if (response.terminal_readers.length > 1) {
            setSelectedReaderId("")
          }
        }
      } catch (error) {
        setErrorMessage("Failed to load card readers")
      }
    }

    useEffect(() => {
      if (order.has_pending_payments) {
        setHasPendingPayments(order.has_pending_payments)
      }
    }, [order])

    useImperativeHandle(
      ref,
      () => ({
        submitPayment: submitPayment,
        paymentType: paymentMethod,
      }),
      [
        paymentMethod,
        stripe,
        elements,
        lineItems,
        campingStyleId,
        isCreditCardFeeEnabled,
        discountSlug,
        isSavingCc,
        isAutoChargingDisabled,
        isAutoCharging,
        isCardReader,
        selectedPaymentMethodId,
        isStore,
        order?.id,
        camp?.id,
        camperId,
      ],
    )

    const handleCardElementChange = (event: StripeCardElementChangeEvent) => {
      const isValidCardInput: boolean = !event.error && event.complete
      setErrorMessage("")
      setIsDisabled(!isValidCardInput)
      if (setIsCardInputValid) setIsCardInputValid(isValidCardInput)
    }

    const handleSubmit = async (event: React.FormEvent) => {
      try {
        await submitPayment(event)
      } catch (err) {
        let error = err as StripeFormError
        if (error.isApiError) {
          if (!error.isStripe) {
            setPaymentStatus("failed")
          }
        }
      }
    }

    useWebsocket(
      (receivedNotification: LiveWebAppNotification) => {
        if (receivedNotification.camp_id != camp.id) return false

        const receivedReservation = receivedNotification.data.reservation
        if (receivedNotification.type == "payment") {
          if (receivedNotification?.data?.payment?.is_pending) {
            setIsProcessing(false)
            setIsDisabled(true)
            setHasPendingPayments(true)
          } else if (receivedReservation.status === STATUSES.PAID && order?.id === receivedReservation?.order_id) {
            setIsDisabled(false)
            setIsProcessing(false)
            setHasPendingPayments(false)
            onSuccess()
          }
        }
      },
      createSubscriptionLiveWebAppNotificationsChannel(),
      [camp?.id, reservationStates, reservationId],
    )

    class StripeFormError extends Error {
      isApiError: boolean
      isStripe: boolean
      error: any

      constructor(message: string, isApiError: boolean = false, isStripe: boolean = false, error: any = null) {
        super(message)
        this.isApiError = isApiError
        this.isStripe = isStripe
        this.error = error
      }

      static fromStripeError(error: any) {
        const message = error?.message || JSON.stringify(error) || "An error occurred"
        return new StripeFormError(message, false, true, error)
      }

      static fromApiError(err: any) {
        let error = err as { is_stripe: boolean; error?: { message?: string }; message?: string }
        const message = error?.error?.message || error?.message || JSON.stringify(error) || "An error occurred"
        return new StripeFormError(message, true, error.is_stripe, error)
      }
    }

    const cancelCurrentTerminalPayment = async (intentId: string) => {
      if (isCardReader && selectedReaderId) {
        api.Terminal.cancelCurrentPayment(camp.id, intentId, selectedReaderId).catch((err) => {
          console.error(err.message)
        })
      }
    }

    const submitPayment = async (
      event: React.FormEvent,
      inputOrder: IOrder | undefined = undefined,
      isGroup?: boolean,
      isTaxFeeEnabledFunc?: boolean,
      isDeposit?: boolean,
    ) => {
      setIsProcessing(true)
      setErrorMessage("")

      const isCardReader = selectedPaymentMethodId === "reader"

      if (event?.preventDefault) {
        event.preventDefault()
      }

      if (!stripe || !elements) {
        return
      }
      
      if (isCardReader && !selectedReaderId) {
        setIsProcessing(false)
        setErrorMessage("Please select a card reader")
        return
      }

      let payment_intent

      const isTaxTrullyEnabled = isTaxFeeEnabledFunc ?? isTaxFeeEnabled
      const orderForRequest = inputOrder || order
      const lineItemsForRequest = inputOrder?.line_items || lineItems
      const partialLineItemsForRequest = depositLineItems ? depositLineItems : []
      const manipulatedLineItems = isGroup
        ? lineItemsForRequest.map(
          (li) => (li.reservation_id && { ...li, reservation_id: orderForRequest.reservation_id }) || li,
        )
        : lineItemsForRequest

      try {
        const resp = await api.Orders.createPaymentIntent({
          order_id: orderForRequest?.id,
          camp_id: camp?.id,
          camper_id: orderForRequest?.camper_id,
          camping_style_id: campingStyleId,
          is_credit_card_fee_enabled: isCreditCardFeeEnabled,
          discount_slug: discountSlug,
          is_storing_credit_card_enabled: isSavingCc,
          is_auto_charging_enabled: isSavingCc && !isAutoChargingDisabled && isAutoCharging,
          is_terminal_payment: isCardReader,
          selected_payment_method_id:
            selectedPaymentMethodId === "new" || selectedPaymentMethodId === "reader"
              ? undefined
              : selectedPaymentMethodId,
          is_store: isStore,
          is_skipping_reservation_status_update: isCreateV2,
          line_items_attributes: manipulatedLineItems.map((li) => mapLineItemToParams(li, isTaxTrullyEnabled)),
          partial_line_items_attributes: partialLineItemsForRequest?.map((li) =>
            mapLineItemToParams(li, isDeposit ? false : isTaxTrullyEnabled),
          ),
        })
        payment_intent = resp.payment_intent
      } catch (err) {
        setIsProcessing(false)
        const error = StripeFormError.fromApiError(err)
        setErrorMessage(error.message)
        throw error
      }

      if (payment_intent.status === "succeeded") {
        setIsProcessing(false)
        onSuccess()
        return
      }

      setPaymentIntentId(payment_intent.id)

      const clientSecret = payment_intent.client_secret

      let result: PaymentIntentResult

      if (isCardReader) {
        setPaymentMethod(ePaymentType.CARD_READER)
        result = await api.Terminal.processPayment(camp.id, payment_intent.id, selectedReaderId)
          .then(async () => {
            let attemptCount = 0
            const maxAttempts = 10

            await new Promise<void>((resolve, reject) => {
              const checkStatus = async () => {
                const result = await api.Terminal.checkPaymentStatus(camp.id, payment_intent.id, selectedReaderId)

                setPaymentStatus(result?.action?.status)

                attemptCount++

                if (result?.action?.status == "succeeded") {
                  if (intervalId) clearInterval(intervalId)
                  setIsProcessing(false)
                  onSuccess()
                  resolve()
                  return
                } else {
                  if (result?.action?.status == "failed" || attemptCount >= maxAttempts) {
                    if (result?.action?.status == "failed") {
                      setErrorMessage(result?.action?.failure_message)
                    } else {
                      setErrorMessage("Max attempts reached")
                    }
                    if (camp.stripe_reader_id) {
                      api.Terminal.cancelCurrentPayment(camp.id, payment_intent.id, selectedReaderId).catch((err) => {
                        reject(new Error(`cancelCurrentPayment ${result?.action?.failure_message}`))
                      })
                    }
                    setPaymentStatus("failed")
                    setIsProcessing(false)
                    if (intervalId) clearInterval(intervalId)
                    reject(new Error("Max attempts reached or payment failed"))
                    return
                  }
                }
              }

              checkStatus()

              let intervalId = setInterval(checkStatus, 5000)
              setIntervalId(intervalId)
            })

            setIsProcessing(false)
            return
          })
          .catch((err) => {
            setErrorMessage(err.message)
            setIsProcessing(false)
            setPaymentStatus("failed")

            throw StripeFormError.fromApiError(err)
          })
        setShowCloseButton(true)
      } else {
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement) as StripeCardElement,
          },
        })
      }

      if (result.error) {
        setIsProcessing(false)
        cancelCurrentTerminalPayment(payment_intent.id)
        const newError = StripeFormError.fromStripeError(result.error)
        setErrorMessage(newError.message)
        throw newError
      } else {
        if (!isCardReader) {
          setIsProcessing(false)

          if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
            onSuccess()
          }
        }
      }
    }
    console.log(hasPendingPayments)
    return (
      <form autoComplete="chrome-off" onSubmit={handleSubmit}>
        {errorMessage && (
          <div className="mb-4">
            <Alert description={errorMessage} />
          </div>
        )}

        <RadioGroup value={selectedPaymentMethodId} onChange={setSelectedPaymentMethodId} className="mb-4">
          <div className="space-y-2 -mt-1.5">
            <PaymentMethodsList
              order={order}
              camperId={camperId}
              paymentMethods={localPaymentMethods}
              setPaymentMethods={setLocalPaymentMethods}
            />
            {isCardReader && readers.map(reader => (
              <RadioGroup.Option 
                key={reader.id} 
                value="reader" 
                data-testid={`reader-option-${reader.id}`}
                onClick={() => {
                  setSelectedReaderId(reader.id)
                  saveReaderToLocalStorage(reader.id, camp?.id)
                }}
              >
                {({ checked }) => (
                  <div
                    className={`py-2 px-3 flex items-center rounded-lg cursor-pointer ${checked && selectedReaderId === reader.id ? "border-2 border-green-600 text-green-600 font-semibold" : "border-gray-300 border"}`}
                  >
                    <WifiIcon className="h-5 w-5 mr-2 rotate-90" />
                    <div>Take payment with {reader.label}</div>
                  </div>
                )}
              </RadioGroup.Option>
            ))}
            {(isCardReader || localPaymentMethods?.length > 0) && (
              <RadioGroup.Option value="new" data-testid="new-option">
                {({ checked }) => (
                  <div
                    className={`py-2 px-3 flex items-center rounded-lg cursor-pointer ${checked ? "border-2 border-green-600 text-green-600 font-semibold" : "border-gray-300 border"}`}
                  >
                    <CreditCardIcon className="w-5 h-5 mr-2" />
                    Add new card
                  </div>
                )}
              </RadioGroup.Option>
            )}
          </div>
        </RadioGroup>

        {selectedPaymentMethodId == "new" && (
          <div className="mb-4 rounded-md border border-gray-200 py-2 px-2.5">
            <CardElement onChange={handleCardElementChange} data-testid="card-element" />
          </div>
        )}

        {(selectedPaymentMethodId == "new" || selectedPaymentMethodId == "reader") && isCamperPresent && (
          <CheckboxWithLabelForForm
            label="Save card securely for future payments"
            value={isSavingCc}
            setValue={setIsSavingCc}
            labelClasses="text-md whitespace-nowrap"
            data-testid="save-card-checkbox"
          />
        )}

        {isOrderRecurring &&
          selectedPaymentMethodId !== null &&
          isCamperPresent &&
          camp?.is_enabled_auto_charging == true && (
            <CheckboxWithLabelForForm
              label="Autocharge future payments to this payment method"
              disabled={isAutoChargingDisabled}
              value={isAutoCharging && isSavingCc}
              setValue={setIsAutoCharging}
              labelClasses="mt-2 text-md whitespace-nowrap"
              data-testid="autocharge-checkbox"
            />
          )}

        {hideCta ? (
          <></>
        ) : (
          <div className="mt-6 flex items-center justify-end">
            <PortalHoverTip
              maxWidth="20rem"
              content={
                <div className="flex items-center gap-4">
                  <ExclamationCircleIcon className="w-10 h-10 ml-auto" />
                  There is already a pending payment for this reservation.
                </div>
              }
              active={hasPendingPayments}
              className="flex items-center"
            >
              <Button
                type="submit"
                className="px-6 flex justify-center items-center"
                variant="blue"
                disabled={
                  !stripe ||
                  !elements ||
                  isProcessing ||
                  Number(order?.total_in_cents) <= (minTotalInCents || 0) ||
                  isDisabled ||
                  isDisabledButton ||
                  hasPendingPayments || 
                  (selectedPaymentMethodId === "reader" && !selectedReaderId)
                }
                dataTestId="submit-button"
              >
                {isProcessing ? (
                  <>
                    <Spinner size={7} className="-mr-1 absolute" />
                    {ctaText}
                  </>
                ) : (
                  ctaText
                )}
              </Button>
            </PortalHoverTip>
          </div>
        )}
      </form>
    )
  },
)

export default StripeCardForm
